import {
  Box,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead
} from '@mui/material';
import { StyledTableCell, StyledTableRow } from '@components/StyledTable';
import { useQueryString } from '@hooks/index';
import { useState } from 'react';
import {
  useFetchInnoBizQuery,
  FetchInnoBizQuery,
  Maybe,
  CapaSortType,
  InnoBizSearchType
} from '@generated/graphql';
import { ArrElement } from '@utils/types';
import { DetailDrawer } from '@components/DetailDrawer';
import { ExpandLess, ExpandMore, HorizontalRule } from '@mui/icons-material';

export type InnoBizData = ArrElement<
  NonNullable<FetchInnoBizQuery['fetchInnoBiz']['data']>['icxModelList']
>;

type InnoBizParamsType = {
  page: string;
  q: string;
  employeesCountSortType: Maybe<CapaSortType>;
  searchType: Maybe<InnoBizSearchType>;
};

const LIMIT = 14;

const InnoBiz = () => {
  const { onUpdateQueryString, query } = useQueryString<InnoBizParamsType>();
  const [drawer, setDrawer] = useState<{
    open: boolean;
    data?: InnoBizData;
  }>({
    open: false
  });
  const page = Number(query.page ?? 1);
  const employeesCountSortType = query.employeesCountSortType ?? null;

  const { data, loading, error } = useFetchInnoBizQuery({
    variables: {
      input: {
        keyword: query.q,
        limit: LIMIT,
        offset: (page - 1) * LIMIT,
        innoBizSearchType: query.searchType,
        employeesCountSortType
      }
    }
  });

  if (loading || error) {
    return null;
  }

  const handleOpenDetailPage = (
    id: string | null | undefined,
    data: InnoBizData
  ) => {
    if (!id) {
      return;
    }

    setDrawer({ open: true, data });
  };

  const handleClickSortType = () => {
    const nextSortType =
      employeesCountSortType === null
        ? CapaSortType.Desc
        : employeesCountSortType === CapaSortType.Desc
        ? CapaSortType.Asc
        : null;

    onUpdateQueryString({ employeesCountSortType: nextSortType });
  };

  const innoBizList = data?.fetchInnoBiz.data?.icxModelList ?? [];
  const innoBizTotalCount = data?.fetchInnoBiz.data?.count ?? 0;

  const SortIcon = getSortIcon(employeesCountSortType);

  return (
    <>
      <TableContainer component={Paper} sx={{ minWidth: 800 }}>
        <Table>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>업체명</StyledTableCell>
              <StyledTableCell>주소</StyledTableCell>
              <StyledTableCell>
                <Box
                  onClick={handleClickSortType}
                  sx={{
                    display: 'flex',
                    ':hover': {
                      cursor: 'pointer'
                    }
                  }}
                >
                  종업원수
                  {SortIcon}
                </Box>
              </StyledTableCell>
              <StyledTableCell>업종</StyledTableCell>
              <StyledTableCell>업종코드</StyledTableCell>
              <StyledTableCell>대표자명</StyledTableCell>
              <StyledTableCell>연락처</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {innoBizList.map((innoBizData) => (
              <StyledTableRow
                onClick={() =>
                  handleOpenDetailPage(innoBizData.id, innoBizData)
                }
                key={innoBizData.id}
                sx={{
                  '&:hover': {
                    cursor: 'pointer',
                    backgroundColor: '#00000030'
                  }
                }}
              >
                <StyledTableCell>{innoBizData.companyName}</StyledTableCell>
                <StyledTableCell>{innoBizData.address}</StyledTableCell>
                <StyledTableCell>{innoBizData.employeesCount}</StyledTableCell>
                <StyledTableCell>{innoBizData.relatedIndustry}</StyledTableCell>
                <StyledTableCell>{innoBizData.industryCode}</StyledTableCell>
                <StyledTableCell>
                  {innoBizData.representativeName}
                </StyledTableCell>
                <StyledTableCell>{innoBizData.companyContact}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        padding={2}
        width={'100%'}
        display={'flex'}
        justifyContent={'center'}
      >
        <Pagination
          count={Math.ceil(innoBizTotalCount / LIMIT)}
          page={page}
          color={'primary'}
          onChange={(e, value) => {
            onUpdateQueryString({ page: `${value}` });
          }}
        />
        <DetailDrawer
          open={drawer.open}
          onClose={() => setDrawer({ open: false })}
          detailInfo={drawer.data}
          tables={[{ title: '기본 정보', table: DefaultTable }]}
        />
      </Box>
    </>
  );
};

export default InnoBiz;

const DefaultTable = {
  companyName: '업체명',
  address: '주소',
  representativeName: '대표자',
  representativeEmai: '대표자 이메일',
  representativeContact: '대표자 HP',
  employeesCount: '종업원 수',
  relatedIndustry: '업종',
  industryCode: '업종 코드',
  product: '주생산품',
  companyContact: '회사 연락처'
};

const getSortIcon = (employeesCountSortType: Maybe<CapaSortType>) => {
  if (!employeesCountSortType) {
    return <HorizontalRule />;
  }
  if (employeesCountSortType === CapaSortType.Desc) {
    return <ExpandMore />;
  }
  return <ExpandLess />;
};
